// Admin → Webhook Endpoints → Deliveries: one endpoint's delivery log. Client-backed, so the server
// sends the most recent `limit` deliveries (MeTable can't page server-side yet); older ones stay in
// the database until the retention sweep removes them.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

import { MeTable } from '@CommonComponents'
import { client } from '@Utils'
import { navigate } from './navigate'
import { Missing, StatusBadge } from './StatusBadge'

const dash = value => (value === null || value === undefined ? '—' : value)

export const tableConfig = (limit, total) => ({
  tableId: 'admin-webhook-deliveries',
  title: 'Deliveries',
  caption: 'Deliveries',
  description:
    total > limit
      ? `The most recent ${limit.toLocaleString()} of ${total.toLocaleString()} deliveries. Payload contents are student data; viewing them is audit logged.`
      : 'Payload contents are student data; viewing them is audit logged.',
  rowKey: 'id',
  columns: [
    {
      key: 'created',
      label: 'Created (UTC)',
      type: 'text',
      sortable: true,
      searchable: true,
      required: true,
      wrap: 'nowrap',
    },
    {
      key: 'event_type',
      label: 'Event',
      type: 'badge',
      sortable: true,
      searchable: true,
      filterable: true,
      value: row => row.event_type,
      searchText: row => row.event_type,
      render: row => (
        <span className="me-badge-tint me-badge-tint-neutral">
          {row.event_type}
        </span>
      ),
    },
    {
      key: 'course',
      label: 'Course',
      type: 'text',
      sortable: true,
      searchable: true,
      value: row => row.course,
      searchText: row => row.course,
      render: row => row.course || <Missing />,
    },
    {
      key: 'status',
      label: 'Status',
      type: 'badge',
      sortable: true,
      searchable: true,
      filterable: true,
      value: row => row.status.label,
      searchText: row => row.status.label,
      render: row => <StatusBadge status={row.status} />,
    },
    {
      key: 'attempt_count',
      label: 'Attempts',
      type: 'numeric',
      sortable: true,
    },
    {
      key: 'next_attempt',
      label: 'Next attempt (UTC)',
      type: 'text',
      sortable: true,
      wrap: 'nowrap',
      value: row => row.next_attempt,
      render: row => row.next_attempt || <Missing />,
    },
  ],
  defaultSort: { key: 'created', direction: 'descending' },
  pageSizes: [25, 50, 100],
  defaultPageSize: 50,
  source: { mode: 'client', max: limit },
  searchLabel: 'Search deliveries',
  searchPlaceholder: 'Search date, event, course, or status',
  emptyState: {
    title: 'No deliveries',
    description: 'No deliveries yet for this endpoint.',
  },
})

// retry re-drives the delivery through the existing POST view, which redirects back with a message
export const submitRetry = (url, csrfToken) => {
  const form = document.createElement('form')
  form.method = 'post'
  form.action = url
  const token = document.createElement('input')
  token.type = 'hidden'
  token.name = 'csrfmiddlewaretoken'
  token.value = csrfToken
  form.appendChild(token)
  document.body.appendChild(form)
  form.submit()
}

export const rowActions = csrfToken => row => {
  const actions = [
    {
      key: 'payload',
      label: 'View payload',
      icon: 'fa-file-code',
      onSelect: () => navigate(row.actions.payload_url),
    },
  ]
  if (row.actions.retry_url) {
    actions.push({
      key: 'retry',
      label: 'Retry',
      icon: 'fa-rotate-right',
      onSelect: () => submitRetry(row.actions.retry_url, csrfToken),
    })
  }
  return actions
}

export const DeliveryDetail = ({ row }) =>
  row.attempts.length ? (
    <div>
      <h4 className="me-ssh">Attempts</h4>
      <table className="table table-condensed w-auto mb-0 small">
        <thead>
          <tr>
            <th>#</th>
            <th>Time (UTC)</th>
            <th>HTTP</th>
            <th>Duration</th>
            <th>Error / response</th>
          </tr>
        </thead>
        <tbody>
          {row.attempts.map(attempt => (
            <tr key={attempt.number}>
              <td>{attempt.number}</td>
              <td className="text-nowrap">{attempt.time}</td>
              <td>{dash(attempt.status_code)}</td>
              <td className="text-nowrap">
                {attempt.duration_ms === null
                  ? '—'
                  : `${attempt.duration_ms} ms`}
              </td>
              <td style={{ wordBreak: 'break-all' }}>{attempt.message}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <p className="small text-muted mb-0">No attempts yet.</p>
  )

DeliveryDetail.propTypes = { row: PropTypes.object.isRequired }

export const DeliveriesTable = ({ dataUrl, limit, csrfToken }) => {
  const [status, setStatus] = useState('loading')
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const config = useMemo(() => tableConfig(limit, total), [limit, total])
  const actions = useMemo(() => rowActions(csrfToken), [csrfToken])

  const load = useCallback(() => {
    setStatus('loading')
    client
      .get(dataUrl)
      .then(response => {
        setRows(response.data.rows)
        setTotal(response.data.total)
        setStatus('ready')
      })
      .catch(() => setStatus('error'))
  }, [dataUrl])

  useEffect(() => load(), [load])

  return (
    <MeTable
      config={config}
      rows={rows}
      status={status}
      onRetry={load}
      rowActions={actions}
      renderDetail={row => <DeliveryDetail row={row} />}
    />
  )
}

DeliveriesTable.propTypes = {
  dataUrl: PropTypes.string.isRequired,
  limit: PropTypes.number.isRequired,
  csrfToken: PropTypes.string.isRequired,
}
